import React, { useState, useEffect, useMemo } from "react";
import Modal from "react-modal";
import Fuse from "fuse.js";
import { Button } from "../Button";
import { Loader } from "../Loader";
import { ReactComponent as CloseIcon } from "../../assets/icons/x.svg";
import { VisaAdminService } from "../../api/generated/services/VisaAdminService";
import { MerchantItem } from "../../api/generated/models/MerchantItem";
import "../../api/openApiConfig";

interface FindBusinessExternalIdModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FindBusinessExternalIdModal: React.FC<FindBusinessExternalIdModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [merchants, setMerchants] = useState<MerchantItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopyVisaMerchantId = async (visaMerchantId: string, contentfulBusinessId: string) => {
    try {
      await navigator.clipboard.writeText(visaMerchantId);
      setCopiedId(contentfulBusinessId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchMerchants();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setError(null);
    }
  }, [isOpen]);

  const fetchMerchants = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await VisaAdminService.postApiVisaAdminListBusinessExternalIds({});
      setMerchants(result.merchants || []);
    } catch (err) {
      setError("Failed to fetch merchants");
      console.error("Error fetching merchants:", err);
    } finally {
      setLoading(false);
    }
  };

  const fuse = useMemo(() => {
    return new Fuse(merchants, {
      keys: ["businessName"],
      threshold: 0.3,
      ignoreLocation: true,
    });
  }, [merchants]);

  const filteredMerchants = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return merchants;
    }

    // Check for exact/prefix matches on contentfulBusinessId and visaMerchantId
    const idMatches = merchants.filter((m) => {
      const contentfulId = (m.contentfulBusinessId || "").toLowerCase();
      const visaMerchantId = (m.visaMerchantId || "").toLowerCase();
      return contentfulId.startsWith(query) || visaMerchantId.startsWith(query);
    });

    // Get fuzzy matches on businessName
    const fuzzyMatches = fuse.search(searchQuery).map((r) => r.item);

    // Combine and deduplicate (id matches first, then fuzzy matches)
    const combined = [...idMatches];
    fuzzyMatches.forEach((item) => {
      if (!combined.some((existing) => existing.contentfulBusinessId === item.contentfulBusinessId)) {
        combined.push(item);
      }
    });

    return combined;
  }, [merchants, searchQuery, fuse]);

  return (
    <Modal
      className="modal-content md:max-w-[1100px]"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Find Business External ID"
    >
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <h2 className="text-primary font-medium text-xl">Find Business External ID</h2>
        <Button onClick={onClose} variant="icon">
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      {/* Search Input */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, Contentful ID, or Visa Merchant ID..."
            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              type="button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader />
        </div>
      ) : error ? (
        <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
          {error}
        </div>
      ) : (
        <div className="max-h-[400px] overflow-y-auto">
          {filteredMerchants.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              {searchQuery ? "No merchants found matching your search" : "No merchants available"}
            </div>
          ) : (
            <table className="table-default w-full">
              <thead>
                <tr>
                  <th className="text-left">Name</th>
                  <th className="text-left">Contentful ID</th>
                  <th className="text-left">Visa Merchant ID</th>
                </tr>
              </thead>
              <tbody>
                {filteredMerchants.map((merchant) => (
                  <tr key={merchant.contentfulBusinessId}>
                    <td className="font-medium">{merchant.businessName || "-"}</td>
                    <td className="text-gray-600 text-sm font-mono">{merchant.contentfulBusinessId || "-"}</td>
                    <td className="text-gray-600 text-sm font-mono">
                      <div className="flex items-center gap-2">
                        <span>{merchant.visaMerchantId || "-"}</span>
                        {merchant.visaMerchantId && (
                          <button
                            onClick={() => handleCopyVisaMerchantId(merchant.visaMerchantId!, merchant.contentfulBusinessId!)}
                            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title="Copy to clipboard"
                            type="button"
                          >
                            {copiedId === merchant.contentfulBusinessId ? (
                              <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
        <span className="text-sm text-gray-500">
          {filteredMerchants.length} merchant{filteredMerchants.length !== 1 ? "s" : ""} found
        </span>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
};

export default FindBusinessExternalIdModal;
